import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Monitor, Users } from "lucide-react";
import MarketCanvas from "@/components/MarketCanvas";
import SectionHeader from "@/components/SectionHeader";
import CourseCard from "@/components/CourseCard";
import ComparisonTable from "@/components/ComparisonTable";
import AccuracyProof from "@/components/AccuracyProof";
import PricingPlans from "@/components/PricingPlans";
import FAQAccordion from "@/components/FAQAccordion";
import Testimonials from "@/components/Testimonials";
import FinalCTA from "@/components/FinalCTA";
import { Reveal, MaskReveal } from "@/components/Reveal";
import { COURSES, COMMUNITY_FEATURES, ENTRADE_DASHBOARD, LEARN_VS_ACCESS, JOURNEY, WHY_ITEMS, STATS, FAQS } from "@/data/content";

const Home = () => {
  useEffect(() => {
    document.title = "T Plus One — Financial Market Education. Built for Serious Traders.";
  }, []);

  return (
    <main>
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <MarketCanvas />
        </div>
        <div className="relative container-x pt-32 pb-20 w-full">
          <Reveal>
            <p className="eyebrow">Financial Market Education</p>
          </Reveal>
          <MaskReveal
            as="h1"
            lines={["Learn the markets.", "Understand the moves."]}
            className="mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] max-w-4xl"
          />
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-dim leading-relaxed">
              From market fundamentals to advanced trading frameworks, T Plus One delivers structured education for traders who want to understand markets at a deeper level.
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/courses"
                data-testid="hero-cta-explore-courses"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink text-ivory px-8 py-4 text-sm font-medium transition-colors hover:bg-pine"
              >
                Explore Courses <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/entrade"
                data-testid="hero-cta-discover-entrade"
                className="link-arrow inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-8 py-4 text-sm font-medium transition-colors hover:border-pine hover:text-pine"
              >
                Discover EnTrade <ArrowUpRight size={15} className="arrow" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.55}>
            <p className="mt-12 font-mono text-[11px] uppercase tracking-[0.18em] text-tertiary">
              Online & Offline Learning · Indian Markets · Forex · Advanced Trading
            </p>
          </Reveal>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block" aria-hidden="true">
          <div className="w-px h-12 bg-ink/15 overflow-hidden">
            <div className="w-px h-4 bg-ink/50 animate-bounce" />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-line bg-white" data-testid="trust-strip">
        <div className="container-x">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border-x border-line">
            {STATS.map((s, i) => (
              <div key={s.label} className="bg-white px-6 py-8 lg:px-10 lg:py-10" data-testid={`trust-stat-${i}`}>
                <Reveal delay={i * 0.08}>
                  <p className="text-3xl lg:text-4xl tracking-tight num">{s.value}</p>
                  <p className="mt-1.5 text-xs text-dim">{s.label}</p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 lg:py-36 border-t border-line" data-testid="intro">
        <div className="container-x">
          <Reveal><p className="eyebrow">T Plus One</p></Reveal>
          <MaskReveal
            as="h2"
            lines={["Trading is easy to access.", "Understanding markets isn’t."]}
            className="mt-6 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] max-w-4xl"
          />
          <Reveal delay={0.25}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-dim leading-relaxed">
              T Plus One exists to bridge that gap through structured financial market education — from foundational concepts to advanced trading methodologies.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COURSE ECOSYSTEM */}
      <section className="py-24 lg:py-32 bg-white border-t border-line" data-testid="courses-section">
        <div className="container-x">
          <SectionHeader
            index="01"
            eyebrow="The Ecosystem"
            lines={["Three courses.", "One learning ecosystem."]}
            sub="Whether someone is entering the markets for the first time or studying advanced execution concepts, T Plus One provides a structured learning path."
          />
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {COURSES.map((c, i) => <CourseCard key={c.slug} course={c} index={i} />)}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-24 lg:py-32 border-t border-line" data-testid="comparison-section">
        <div className="container-x">
          <SectionHeader index="02" eyebrow="Compare" lines={["Side by side."]} sub="Every course follows the same structured philosophy — the difference is the market and the depth." />
          <div className="mt-14">
            <ComparisonTable />
          </div>
        </div>
      </section>

      {/* PREMIUM COMMUNITY — DARK */}
      <section id="community" className="relative bg-coal text-ivory py-24 lg:py-36 grain overflow-hidden scroll-mt-20" data-testid="community-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
            <div>
              <Reveal><p className="eyebrow-dark">Premium Community</p></Reveal>
              <MaskReveal
                as="h2"
                lines={["The course doesn’t end", "when the class does."]}
                className="mt-6 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
              />
              <Reveal delay={0.2}>
                <p className="mt-8 text-base sm:text-lg text-[#A0A09C] leading-relaxed max-w-md">
                  Students who enrol in TradeX, TradeFX or ExpertIN receive lifetime access to the T Plus One Premium Community.
                </p>
              </Reveal>
              <Reveal delay={0.3} className="mt-10">
                <AccuracyProof dark />
              </Reveal>
            </div>
            <div className="grid sm:grid-cols-2 gap-px bg-coalline border border-coalline rounded-sm overflow-hidden">
              {COMMUNITY_FEATURES.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.06} className="bg-coal">
                  <div className="p-7 h-full" data-testid={`community-feature-${i}`}>
                    <p className="font-mono text-[10px] text-pine-2 tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="mt-3 text-base font-medium tracking-tight">{f.title}</h3>
                    <p className="mt-2 text-sm text-[#A0A09C] leading-relaxed">{f.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ENTRADE REVEAL */}
      <section className="py-24 lg:py-36 border-t border-line" data-testid="entrade-reveal">
        <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <Reveal><p className="eyebrow">T Plus One Membership</p></Reveal>
            <MaskReveal as="h2" lines={["EnTrade.", "Stay closer to the market."]} className="mt-6 text-4xl sm:text-5xl tracking-tight leading-[1.08]" />
            <Reveal delay={0.2}>
              <p className="mt-8 text-base sm:text-lg text-dim leading-relaxed max-w-md">
                EnTrade is T Plus One’s completely online membership designed for traders who want ongoing access to the T Plus One Premium Community, market insights and trade recommendations without enrolling in a full course.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/entrade" data-testid="entrade-reveal-cta" className="link-arrow mt-8 inline-flex items-center gap-2 text-sm font-medium text-pine">
                Explore EnTrade <ArrowRight size={15} className="arrow" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="rounded-sm border border-line bg-white shadow-[0_32px_80px_-32px_rgba(16,16,16,0.2)] overflow-hidden" data-testid="entrade-dashboard-preview">
              <div className="flex items-center gap-2 border-b border-line px-5 py-3.5">
                <span className="w-2 h-2 rounded-full bg-line" /><span className="w-2 h-2 rounded-full bg-line" /><span className="w-2 h-2 rounded-full bg-pine/60" />
                <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.18em] text-tertiary">EnTrade · Premium Community</span>
              </div>
              <div className="grid sm:grid-cols-2">
                {ENTRADE_DASHBOARD.map((d, i) => (
                  <div key={d.label} className={`p-6 ${i % 2 === 0 ? "sm:border-r" : ""} ${i < 4 ? "border-b" : ""} border-line`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-tertiary">{d.label}</p>
                    <p className="mt-2 text-sm font-medium">{d.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 lg:py-32 bg-white border-t border-line" data-testid="pricing-section">
        <div className="container-x">
          <SectionHeader index="03" eyebrow="EnTrade Pricing" lines={["Choose your access."]} sub="Every plan includes the Premium Community, market recommendations and insights — delivered entirely online." />
          <div className="mt-14">
            <PricingPlans />
          </div>
        </div>
      </section>

      {/* LEARN VS ACCESS */}
      <section className="py-24 lg:py-32 border-t border-line" data-testid="learn-vs-access">
        <div className="container-x">
          <SectionHeader eyebrow="Which One" lines={["Learning or access?", "Choose what you need."]} />
          <div className="mt-14 grid md:grid-cols-2 gap-5">
            {[LEARN_VS_ACCESS.courses, LEARN_VS_ACCESS.entrade].map((side, i) => (
              <Reveal key={side.title} delay={i * 0.12} className="h-full">
                <div className={`h-full rounded-sm border p-8 lg:p-10 ${i === 1 ? "bg-coal text-ivory border-coal" : "bg-white border-line"}`} data-testid={`lva-${side.title.toLowerCase()}`}>
                  <p className={i === 1 ? "eyebrow-dark" : "eyebrow"}>{side.title}</p>
                  <p className={`mt-5 text-xl sm:text-2xl tracking-tight leading-snug ${i === 1 ? "text-[#D4D4D0]" : "text-ink"}`}>{side.audience}</p>
                  <ul className={`mt-8 pt-6 border-t space-y-3 ${i === 1 ? "border-coalline" : "border-line"}`}>
                    {side.items.map((it) => (
                      <li key={it} className={`text-sm flex gap-3 ${i === 1 ? "text-[#A0A09C]" : "text-dim"}`}>
                        <span className="text-pine-2 mt-0.5">—</span>{it}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={i === 1 ? "/entrade" : "/courses"}
                    data-testid={`lva-cta-${side.title.toLowerCase()}`}
                    className={`link-arrow mt-8 inline-flex items-center gap-2 text-sm font-medium ${i === 1 ? "text-ivory" : "text-pine"}`}
                  >
                    {i === 1 ? "Discover EnTrade" : "Explore Courses"} <ArrowRight size={15} className="arrow" />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING JOURNEY */}
      <section className="py-24 lg:py-36 bg-white border-t border-line" data-testid="journey-section">
        <div className="container-x">
          <SectionHeader index="04" eyebrow="How It Works" lines={["The learning experience."]} />
          <div className="mt-16">
            {JOURNEY.map((step, i) => (
              <Reveal key={step.n} delay={0.05}>
                <div
                  className={`group grid grid-cols-[auto_1fr] sm:grid-cols-[120px_1fr_1fr] gap-6 items-baseline py-10 lg:py-14 ${i > 0 ? "border-t border-line" : ""}`}
                  data-testid={`journey-step-${step.n}`}
                >
                  <span className="font-mono text-sm text-pine transition-transform duration-500 group-hover:translate-x-1">{step.n}</span>
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight transition-colors duration-500 group-hover:text-pine">{step.title}</h3>
                  <p className="col-start-2 sm:col-start-3 text-base text-dim leading-relaxed max-w-sm">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ONLINE + OFFLINE */}
      <section className="border-t border-line" data-testid="online-offline">
        <div className="grid lg:grid-cols-2">
          {[
            { icon: Monitor, title: "Learn Online", text: "Flexible access for students who prefer remote learning.", testid: "learn-online" },
            { icon: Users, title: "Learn Offline", text: "Classroom-based learning for students who prefer direct interaction.", testid: "learn-offline" },
          ].map((s, i) => (
            <div key={s.title} className={`relative py-20 lg:py-32 px-5 sm:px-8 lg:px-16 ${i === 1 ? "bg-coal text-ivory grain" : "bg-ivory"}`} data-testid={s.testid}>
              <div className="max-w-md mx-auto lg:mx-0 lg:ml-auto lg:mr-0" style={i === 0 ? { marginLeft: "auto", marginRight: 0 } : {}}>
                <s.icon size={22} className={i === 1 ? "text-pine-2" : "text-pine"} aria-hidden="true" />
                <h3 className="mt-6 text-3xl lg:text-4xl tracking-tight">{s.title}</h3>
                <p className={`mt-4 text-base leading-relaxed ${i === 1 ? "text-[#A0A09C]" : "text-dim"}`}>{s.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-subtle border-t border-line py-16 text-center container-x">
          <Reveal>
            <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl tracking-tight">One curriculum. Two ways to learn.</p>
            <p className="mt-5 text-sm text-dim max-w-xl mx-auto leading-relaxed">
              TradeX, TradeFX and ExpertIN support both online and offline learning. EnTrade remains completely online.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY T PLUS ONE */}
      <section className="py-24 lg:py-36 border-t border-line" data-testid="why-section">
        <div className="container-x">
          <Reveal>
            <div className="flex items-center gap-4">
              <span className="font-mono text-[11px] tracking-[0.22em] text-pine">05</span>
              <p className="eyebrow">Why T Plus One</p>
              <span className="h-px flex-1 max-w-16 bg-line" aria-hidden="true" />
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-x-16">
            {WHY_ITEMS.map((w, i) => (
              <Reveal key={w.title} delay={(i % 2) * 0.1}>
                <div className={`py-12 ${i > 1 ? "" : ""} border-t border-line`} data-testid={`why-item-${i}`}>
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">{w.title}</h3>
                  <p className="mt-4 text-base text-dim leading-relaxed max-w-md">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32 bg-white border-t border-line" data-testid="testimonials-section">
        <div className="container-x">
          <SectionHeader index="06" eyebrow="Students" lines={["From the community."]} />
          <div className="mt-14">
            <Testimonials />
          </div>
        </div>
      </section>

      {/* STATS — moved to trust strip under hero */}

      {/* ABOUT TEASER */}
      <section className="py-24 lg:py-32 bg-white border-t border-line" data-testid="about-teaser">
        <div className="container-x max-w-4xl">
          <Reveal><p className="eyebrow">About</p></Reveal>
          <MaskReveal as="h2" lines={["Built to make market education", "more structured."]} className="mt-6 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12]" />
          <Reveal delay={0.2}>
            <p className="mt-8 text-base sm:text-lg text-dim leading-relaxed max-w-2xl">
              T Plus One is an EdTech company focused on financial markets — combining structured courses with continued community-based market learning, so education doesn’t stop when a program ends.
            </p>
            <Link to="/about" data-testid="about-teaser-cta" className="link-arrow mt-8 inline-flex items-center gap-2 text-sm font-medium text-pine">
              More about T Plus One <ArrowRight size={15} className="arrow" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 border-t border-line" data-testid="faq-section">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20">
          <div>
            <SectionHeader eyebrow="FAQ" lines={["Questions,", "answered plainly."]} />
            <Reveal delay={0.2}>
              <Link to="/faq" data-testid="faq-more-link" className="link-arrow mt-8 inline-flex items-center gap-2 text-sm font-medium text-pine">
                View all questions <ArrowRight size={15} className="arrow" />
              </Link>
            </Reveal>
          </div>
          <FAQAccordion items={FAQS.slice(0, 6)} />
        </div>
      </section>

      <FinalCTA />
    </main>
  );
};

export default Home;
